import { LogLevel, initializeFaro, getWebInstrumentations, ConsoleInstrumentation, faro } from '@grafana/faro-web-sdk';
export { LogLevel } from '@grafana/faro-web-sdk';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';

function bootstrapNavigationTracking(tnAnalytics) {
    setupSPATracking(tnAnalytics);
}
/**
 * Debounce navigation tracking to avoid tracking multiple times in quick succession
 */
const DEBOUNCE_INTERVAL = 100;
let lastTrackedTime = 0;
const trackNavigationWithDebounce = (tnAnalytics, navHistory) => {
    const now = Date.now();
    if (now - lastTrackedTime > DEBOUNCE_INTERVAL) {
        let current = '';
        let previous = '';
        if (navHistory.current) {
            current = navHistory.current.endsWith("/") && navHistory.current.length > 1 ? navHistory.current.slice(0, -1) : navHistory.current;
        }
        if (navHistory.previous) {
            previous = navHistory.previous.endsWith("/") && navHistory.previous.length > 1 ? navHistory.previous.slice(0, -1) : navHistory.previous;
        }
        tnAnalytics.trackNavigation({
            previous,
            current,
        });
        lastTrackedTime = now;
    }
};
// Setup tracking for single-page applications (SPAs) with pushState/replaceState
function setupSPATracking(tnAnalytics) {
    var _a, _b;
    trackNavigationWithDebounce(tnAnalytics, {
        previous: "",
        current: removeQueryParams((_b = (_a = window.history) === null || _a === void 0 ? void 0 : _a.state) === null || _b === void 0 ? void 0 : _b.current),
    });
    const originalPushState = history.pushState;
    history.pushState = function (...args) {
        var _a, _b;
        originalPushState.apply(history, args);
        trackNavigationWithDebounce(tnAnalytics, {
            previous: removeQueryParams((_a = history === null || history === void 0 ? void 0 : history.state) === null || _a === void 0 ? void 0 : _a.back),
            current: removeQueryParams((_b = history === null || history === void 0 ? void 0 : history.state) === null || _b === void 0 ? void 0 : _b.current),
        });
    };
    // Add popstate listener for back/forward navigation
    window.addEventListener("popstate", (event) => {
        var _a, _b;
        trackNavigationWithDebounce(tnAnalytics, {
            previous: removeQueryParams((_a = event === null || event === void 0 ? void 0 : event.state) === null || _a === void 0 ? void 0 : _a.back),
            current: removeQueryParams((_b = event === null || event === void 0 ? void 0 : event.state) === null || _b === void 0 ? void 0 : _b.current),
        });
    });
}
function removeQueryParams(url) {
    if (!url || typeof url !== "string")
        return "";
    return url.split("?")[0];
}
// Clean up listeners when page is unloaded
function cleanupNavigationTracking(tnAnalytics) {
    window.removeEventListener("popstate", () => tnAnalytics.trackNavigation({ previous: "", current: "" }));
    window.removeEventListener("hashchange", () => tnAnalytics.trackNavigation({ previous: "", current: "" }));
}

var ConsentOptions;
(function (ConsentOptions) {
    ConsentOptions["ALWAYS_ON"] = "always_on";
    ConsentOptions["ANALYTICS"] = "analytics";
    ConsentOptions["BIG_DATA"] = "big_data";
    ConsentOptions["PERSONALIZATION"] = "personalization";
    ConsentOptions["MOBILE"] = "mobile";
    ConsentOptions["ENGAGEMENT"] = "engagement";
    ConsentOptions["MONITORING"] = "monitoring";
})(ConsentOptions || (ConsentOptions = {}));
var TrackEvenTypes;
(function (TrackEvenTypes) {
    TrackEvenTypes["CLICK"] = "click";
    TrackEvenTypes["NAVIGATION"] = "navigation";
    TrackEvenTypes["ButtonClick"] = "button";
    TrackEvenTypes["CheckboxClick"] = "checkbox";
    TrackEvenTypes["InputFocused"] = "input_focused";
    TrackEvenTypes["DropdownOpened"] = "dropdown_opened";
    TrackEvenTypes["ModalOpened"] = "modal_opened";
    TrackEvenTypes["FormSubmitted"] = "form_submitted";
})(TrackEvenTypes || (TrackEvenTypes = {}));
var LogType;
(function (LogType) {
    LogType["ERROR"] = "error";
    LogType["INFO"] = "info";
    LogType["LOG"] = "log";
})(LogType || (LogType = {}));

function randomStr(length = 12) {
    const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";
    for (let i = 0; i < length; i++) {
        result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return result;
}
const Log = (message, type = LogType.INFO) => {
    if (process.env.NODE_ENV === "production" || process.env.NODE_ENV === 'prod')
        return;
    if (type === LogType.ERROR) {
        console.error(message);
        return;
    }
    if (type === LogType.INFO) {
        console.info(message);
        return;
    }
    console.log(message);
};

class TnAnalytics {
    constructor(config) {
        var _a, _b;
        this.instrumentations = [];
        this.faroConfig = config.faroConfig;
        this.autoTrackClicks = (_a = config.autoTrackClicks) !== null && _a !== void 0 ? _a : false;
        this.autoTrackNavigation = (_b = config.autoTrackNavigation) !== null && _b !== void 0 ? _b : false;
        this.deviceType = this.detectDeviceType();
        this.browserVersion = this.detectBrowser();
        this.operatingSystem = this.detectOperatingSystem();
        if (config.requiredConsent) {
            Log("Analytics: Consent required, checking user decision", LogType.INFO);
            this.requiredConsent = config.requiredConsent;
            this.initializeOnUserConsent();
        }
        else {
            Log("Analytics: No consent required, initializing", LogType.INFO);
            this.initialize();
        }
    }
    static create(config) {
        const validConfig = TnAnalytics.checkConfig(config);
        if (!validConfig)
            return;
        return new TnAnalytics(config);
    }
    static checkConfig(config) {
        var _a, _b, _c, _d, _e, _f;
        if (!((_a = config.faroConfig) === null || _a === void 0 ? void 0 : _a.apiKey)) {
            Log("Analytics: Missing API key", LogType.ERROR);
            return false;
        }
        if (!((_b = config.faroConfig) === null || _b === void 0 ? void 0 : _b.url)) {
            Log("Analytics: Missing faro url", LogType.ERROR);
            return false;
        }
        if (((_c = config.faroConfig) === null || _c === void 0 ? void 0 : _c.disabledLogLevels) && !Array.isArray((_d = config.faroConfig) === null || _d === void 0 ? void 0 : _d.disabledLogLevels)) {
            Log("Analytics: disabledLogLevels should be an array of LogLevel", LogType.ERROR);
            return false;
        }
        if (!((_f = (_e = config.faroConfig) === null || _e === void 0 ? void 0 : _e.app) === null || _f === void 0 ? void 0 : _f.name)) {
            Log("Analytics: Missing App name", LogType.ERROR);
            return false;
        }
        if ((config === null || config === void 0 ? void 0 : config.requiredConsent) && !Array.isArray(config === null || config === void 0 ? void 0 : config.requiredConsent)) {
            Log("Analytics: requiredConsent should be an array of ConsentOptions", LogType.ERROR);
            return false;
        }
        return true;
    }
    initializeOnUserConsent() {
        var _a;
        const hasConsented = (_a = document === null || document === void 0 ? void 0 : document.cookie) === null || _a === void 0 ? void 0 : _a.includes("consent:true");
        if (hasConsented) {
            Log("Analytics: User has consent cookie, initializing", LogType.INFO);
            return this.initialize();
        }
        else {
            window.addEventListener("consent_updated", (e) => {
                var _a, _b;
                const event = e;
                if (((_a = event === null || event === void 0 ? void 0 : event.detail) === null || _a === void 0 ? void 0 : _a.decision) && Array.isArray((_b = event === null || event === void 0 ? void 0 : event.detail) === null || _b === void 0 ? void 0 : _b.decision)) {
                    Log("Analytics: User accepted consent, initializing", LogType.INFO);
                    const userDecision = event.detail.decision;
                    const requiredConsent = this.requiredConsent;
                    if (
                    // Check if all required consents are present in user decision
                    Array.isArray(requiredConsent) &&
                        requiredConsent.every((consent) => userDecision.includes(consent))) {
                        this.initialize();
                    }
                }
                else {
                    Log("Analytics: User declined analytics consent", LogType.INFO);
                }
            });
        }
    }
    initialize() {
        this.getOrSetSession();
        if (this.faroConfig) {
            Log("Analytics: Initializing faro", LogType.INFO);
            this.initializeFaroAnalytics();
        }
        if (this.autoTrackClicks)
            this.initializeClickTracker();
        if (this.autoTrackNavigation)
            this.initializeNavigationTracking(this);
    }
    initializeFaroAnalytics() {
        var _a, _b, _c;
        this.setInstrumentations();
        initializeFaro({
            url: (_a = this.faroConfig) === null || _a === void 0 ? void 0 : _a.url,
            apiKey: (_b = this.faroConfig) === null || _b === void 0 ? void 0 : _b.apiKey,
            instrumentations: this.instrumentations,
            app: (_c = this.faroConfig) === null || _c === void 0 ? void 0 : _c.app,
        });
    }
    setInstrumentations() {
        var _a, _b, _c, _d;
        this.instrumentations.push(...getWebInstrumentations({ captureConsole: true }));
        this.instrumentations.push(new ConsoleInstrumentation({
            disabledLevels: (_a = this.faroConfig) === null || _a === void 0 ? void 0 : _a.disabledLogLevels,
        }));
        if (!((_b = this.faroConfig) === null || _b === void 0 ? void 0 : _b.disableTracing) && !((_d = (_c = this.faroConfig) === null || _c === void 0 ? void 0 : _c.disabledLogLevels) === null || _d === void 0 ? void 0 : _d.includes(LogLevel.TRACE))) {
            this.instrumentations.push(new TracingInstrumentation());
        }
    }
    initializeClickTracker() {
        document.addEventListener("click", this.clickHandler);
        document.addEventListener("beforeunload", () => {
            document.removeEventListener("click", this.clickHandler);
        });
    }
    initializeNavigationTracking(self) {
        bootstrapNavigationTracking(self);
        window.addEventListener("beforeunload", () => cleanupNavigationTracking(self));
    }
    detectDeviceType() {
        const userAgent = (navigator === null || navigator === void 0 ? void 0 : navigator.userAgent) || "unknown";
        if (/mobile/i.test(userAgent))
            return "Mobile";
        if (/iPad|Android|Touch/i.test(userAgent))
            return "Tablet";
        return "Desktop";
    }
    detectBrowser() {
        const ua = (navigator === null || navigator === void 0 ? void 0 : navigator.userAgent) || "unknown";
        if (/MSIE|Trident/.test(ua))
            return "Internet Explorer";
        if (/Chrome/.test(ua) && !/Edg/.test(ua))
            return "Chrome";
        if (/Firefox/.test(ua))
            return "Firefox";
        if (/Safari/.test(ua) && !/Chrome/.test(ua))
            return "Safari";
        if (/Edg/.test(ua))
            return "Edge";
        return "Unknown";
    }
    detectOperatingSystem() {
        var _a;
        const ua = ((_a = navigator === null || navigator === void 0 ? void 0 : navigator.userAgent) === null || _a === void 0 ? void 0 : _a.toLowerCase()) || "unknown";
        if (ua.includes("win"))
            return "Windows";
        if (ua.includes("mac"))
            return "Mac";
        if (ua.includes("linux"))
            return "Linux";
        return "Unknown";
    }
    static track(eventName, attributes) {
        var _a;
        Log(`Analytics: Event - ${eventName}`);
        (_a = faro === null || faro === void 0 ? void 0 : faro.api) === null || _a === void 0 ? void 0 : _a.pushEvent(eventName, attributes);
    }
    clickHandler(event) {
        let el = event === null || event === void 0 ? void 0 : event.target;
        if (!el)
            return;
        // Loop through each parent element to check if the data-tnkey attribute exists
        let count = 0;
        const maxCount = 20;
        while (el && count < maxCount) {
            count++;
            if (el.hasAttribute("data-tnkey")) {
                const key = el.getAttribute("data-tnkey");
                let val = el.getAttribute("data-tnvalue");
                if (!val) {
                    val = el.innerText;
                }
                const clickPayload = {
                    key: key || "",
                    value: val || "",
                    device: this.deviceType,
                    browser: this.browserVersion,
                    os: this.operatingSystem,
                };
                TnAnalytics.track(TrackEvenTypes.CLICK, clickPayload);
                break;
            }
            el = el.parentElement;
        }
    }
    static getSession() {
        const randomId = randomStr();
        const now = Date.now();
        if (!localStorage || typeof localStorage === "undefined") {
            return {
                id: randomId,
                time: now,
            };
        }
        const session = localStorage === null || localStorage === void 0 ? void 0 : localStorage.getItem("tnAnalytics");
        if (session) {
            const tnAnalyticsSession = JSON.parse(session);
            if (tnAnalyticsSession &&
                TnAnalytics.isSessionValid(tnAnalyticsSession.time)) {
                return tnAnalyticsSession;
            }
        }
        TnAnalytics.setSessionId(randomId, now);
        return {
            id: randomId,
            time: now,
        };
    }
    static isSessionValid(time) {
        return time && time > TnAnalytics.getMaxSessionTime();
    }
    static getMaxSessionTime() {
        return Date.now() - 60 * 5 * 1000; // 5 minutes session
    }
    static setSessionId(id, time) {
        localStorage.setItem("tnAnalytics", JSON.stringify({ id, time }));
    }
    // Session identifier, changes every 5 minutes
    getOrSetSession() {
        if (this.tnAnalyticsSession &&
            TnAnalytics.isSessionValid(this.tnAnalyticsSession.time)) {
            return this.tnAnalyticsSession;
        }
        return TnAnalytics.getSession();
    }
    // Unique device id, does not change
    getOrSetTnAnalyticsUser() {
        let tnAnalyticsUser = {
            id: randomStr(18),
        };
        if (!sessionStorage || typeof sessionStorage === "undefined") {
            return tnAnalyticsUser;
        }
        const hasExistingUser = sessionStorage.getItem("tnAnalyticsUser");
        if (hasExistingUser) {
            tnAnalyticsUser = JSON.parse(hasExistingUser);
        }
        if (!hasExistingUser) {
            sessionStorage.setItem("tnAnalyticsUser", JSON.stringify(tnAnalyticsUser));
        }
        return tnAnalyticsUser;
    }
    updateSessionTime() {
        const tnAnalyticsSession = this.getOrSetSession();
        const newTime = Date.now();
        this.tnAnalyticsSession = { id: tnAnalyticsSession.id, time: newTime };
        TnAnalytics.setSessionId(tnAnalyticsSession.id, newTime);
    }
    trackNavigation(navHistory) {
        const tnAnalyticsSession = this.getOrSetSession();
        const tnAnalyticsUser = this.getOrSetTnAnalyticsUser();
        const timeOnPage = tnAnalyticsSession.time
            ? (Date.now() - tnAnalyticsSession.time) / 1000
            : 0;
        const navPayload = {
            sessionId: tnAnalyticsSession.id,
            userId: tnAnalyticsUser.id,
            device: this.deviceType,
            browser: this.browserVersion,
            os: this.operatingSystem,
            previous_uri: navHistory.previous || "",
            current_uri: navHistory.current || "",
            timeOnPage: timeOnPage.toString(),
        };
        TnAnalytics.track(TrackEvenTypes.NAVIGATION, navPayload);
        this.updateSessionTime();
    }
    static generateNavigationPayload(pageview) {
        return {
            sessionId: pageview.sessionId,
            userId: pageview.userId,
            device: pageview.device || "Desktop",
            browser: pageview.browser || "Unknown",
            os: pageview.os || "Unknown",
            previous_uri: pageview.previous_uri,
            current_uri: pageview.current_uri,
            timeOnPage: pageview.timeOnPage ? pageview.timeOnPage.toString() : "0",
        };
    }
}
var index = {
    TnAnalytics,
    LogLevel,
    ConsentOptions,
};

export { ConsentOptions, TnAnalytics, index as default };
//# sourceMappingURL=index.js.map
